import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getEnabledElement } from '../state';
import {
  getAnnotationPlan,
  refreshElement,
  removeAnnotations,
} from '../utils/annotationToolState';

/**
 * Logic of the Reset toolbar button.
 *
 * A single state slot, `plan`, drives the whole flow: `null` means the modal is
 * closed, a plan means it is open and already knows what it would delete. The
 * plan is computed in the click handler and never at render time, because
 * `ToolbarRow` re-renders on every image change and scanning the global tool
 * state on each of those renders would be a needless walk.
 *
 * `activeViewportIndex` is a number, so subscribing to it cannot produce the
 * identity-change re-renders a derived object selector would.
 *
 * The element is captured in a ref rather than read again on confirmation: the
 * active viewport may have changed while the modal was open, and the repaint has
 * to hit the element the plan was built from.
 *
 * None of the handlers is memoized on purpose. `toolbarClickCallback` is a fresh
 * `.bind(this)` on every `ToolbarRow` render (`ToolbarRow.js:302`), so any
 * `useCallback` keyed on it would be invalidated every time anyway, and would
 * only add a dependency comparison for no reuse.
 */
const useResetToolbarButton = ({ button, toolbarClickCallback }) => {
  const activeViewportIndex = useSelector(
    state => state.viewports.activeViewportIndex
  );
  const [plan, setPlan] = useState(null);
  const plannedElementRef = useRef(null);
  const declineRef = useRef(null);

  // Runs the button's own command, ie. the plain reset the toolbar declares.
  const reset = evt => toolbarClickCallback(button, evt);

  // Opens the prompt only when there is something to delete, resets otherwise.
  const requestReset = evt => {
    const element = getEnabledElement(activeViewportIndex);
    const nextPlan = getAnnotationPlan(element);

    if (nextPlan.toolTypes.length === 0) {
      reset(evt);
      return;
    }

    plannedElementRef.current = element;
    setPlan(nextPlan);
  };

  // Deletes the planned annotations, repaints, then resets the viewport.
  const confirmDelete = () => {
    setPlan(null);
    removeAnnotations(plan);
    refreshElement(plannedElementRef.current);
    reset();
  };

  // Keeps the annotations and resets the viewport, the historical behaviour.
  const declineDelete = () => {
    setPlan(null);
    reset();
  };

  // Closes the prompt without resetting nor deleting anything.
  const cancel = () => setPlan(null);

  /**
   * Moves the initial focus onto the safe choice.
   *
   * Left alone, Radix focuses the first tabbable child of the dialog, which is
   * the close cross AZModal renders ahead of this body. React flushes effects
   * child-first and Radix's FocusScope lives in a descendant of this component,
   * so its focus effect runs first and this one overrides it.
   *
   * Reservation, not settled without a browser test: should Radix ever defer its
   * autofocus to a rAF or a microtask, it would land after this effect and win.
   * The fallout stays benign, the cross would take focus and Enter would just
   * close the modal, because "Yes" is never the first tabbable element.
   */
  useEffect(() => {
    if (plan && declineRef.current) {
      declineRef.current.focus();
    }
  }, [plan]);

  return {
    plan,
    declineRef,
    requestReset,
    confirmDelete,
    declineDelete,
    cancel,
  };
};

export { useResetToolbarButton };
